import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { db } from '@/config/firebase';
import { sanitizeDocIdSync, validateFirestorePath } from '@/utils/pmt2PathUtils';
// Import necessary functions
import { collection, getDocs, doc, getDoc, DocumentData, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';

interface PPCBasicsDashboardProps {
  product: PMT2Product | null;
}

// Define the structure for the old values map
type OldValuesMap = Record<string, string>;

// --- Formatting & Utility Helpers ---
const parseSafeFloat = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Timestamp) return value.seconds;
    const cleanedValue = String(value).replace(/[$,%]/g, '').replace(/,/g, '');
    const num = parseFloat(cleanedValue);
    return isNaN(num) ? null : num;
};

const formatDefault = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (value instanceof Timestamp) return formatDateMMDDYYYY(value.toDate());
    return String(value);
};

// Converts Excel Serial Date Number to JavaScript Date Object
const excelSerialDateToJSDate = (serial: number | null | undefined): Date | null => {
    if (serial === null || serial === undefined || isNaN(serial) || serial < 1) return null;
    const excelEpochDiff = 25567;
    const daysSinceJSEpoch = serial - excelEpochDiff;
    const milliseconds = daysSinceJSEpoch * 24 * 60 * 60 * 1000;
    return new Date(milliseconds);
};


const formatDateMMDDYYYY = (date: Date | null): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '-';
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${month}/${day}/${year}`;
};

const formatNumber = (value: any, decimals = 0): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
     return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};
// --- End Helpers ---

// --- Item Display Component ---
interface ItemDisplayProps {
    label: string;
    value: React.ReactNode; // Current display value
    currentValue?: any; // Raw current value for comparison
    oldValue?: string | null; // Raw old value from map
    isLoading: boolean;
    colorClass?: string;
    icon?: React.ReactNode;
    labelKey?: string; // Key used to look up in oldValuesMap (usually same as label)
}
const ItemDisplay: React.FC<ItemDisplayProps> = ({
    label,
    value,
    currentValue,
    oldValue,
    isLoading,
    colorClass = '',
    icon = null,
    labelKey // Use labelKey if provided, otherwise default to label
}) => {
    const keyForOldValue = labelKey ?? label; // Determine the key to use

    // Format the old value for display, handling special cases like dates
    const formatOldValue = (key: string, val: string | null | undefined): string => {
        if (val === null || val === undefined || val === '') return '-';
        if (key === 'Start Date') {
            const dateNum = parseSafeFloat(val);
            return formatDateMMDDYYYY(excelSerialDateToJSDate(dateNum));
        }
        if (key === 'Total ASIN Reviews' || key === 'ASIN Review Cadence' || key === 'Days Since Last Review') {
             const num = parseSafeFloat(val);
             return num !== null ? Math.round(num).toString() : '-';
        }
        return formatDefault(val);
    };

    const formattedOldValue = formatOldValue(keyForOldValue, oldValue);
    const displayOldValue = oldValue !== null && oldValue !== undefined && oldValue !== '' && oldValue !== '-' && String(currentValue) !== String(oldValue);

    return (
     <TableRow>
        <TableCell className="font-medium py-2 px-4">{label}</TableCell>
        <TableCell className={`text-right py-2 px-4 ${colorClass}`}>
            {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                 <div className="flex items-center justify-end space-x-1">
                    {icon}
                    <span>{value}</span>
                </div>
            )}
        </TableCell>
        {/* Old Value Cell */}
        <TableCell className="text-right py-2 px-4">
             {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                 displayOldValue ? (
                     <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                         {formattedOldValue}
                     </span>
                 ) : (
                     <span>-</span>
                 )
             )}
        </TableCell>
    </TableRow>
    );
};
// --- End Item Display ---


const PPCBasicsDashboard: React.FC<PPCBasicsDashboardProps> = ({ product }) => {
  const [totalASINReviews, setTotalASINReviews] = useState<number | null>(null);
  const [biddingGroupURL, setBiddingGroupURL] = useState<string | null>(null);
  const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null); // State for old values
  const [loadingExtra, setLoadingExtra] = useState<boolean>(true);

  // Fetch extra data (reviews count, bidding URL, old values map)
  useEffect(() => {
    const fetchExtraData = async () => {
      // Reset state
      setTotalASINReviews(null);
      setBiddingGroupURL(null);
      setOldValuesMap(null);
      setLoadingExtra(true);

      if (!product || !product.companyName || !product.id) {
        setLoadingExtra(false);
        return;
      }

      let reviewCount = 0;
      let bidUrl: string | null = null;
      let fetchedOldValues: OldValuesMap | null = null;

      try {
        // Use Promise.allSettled to fetch concurrently and handle individual errors
        const results = await Promise.allSettled([
            // Fetch Reviews Count - Revised Logic: Get doc and count entries array
            (async () => {
                const docId = 'Standard ASIN Review'; // Document ID matches Data Element
                
                // Sanitize path components to prevent path length errors
                const sanitizedAsin = sanitizeDocIdSync(product.id, {
                  maxLength: 100,
                  context: 'ppc-basics-asin'
                });
                const sanitizedDocId = sanitizeDocIdSync(docId, {
                  maxLength: 100,
                  context: 'ppc-basics-docId'
                });
                
                // Validate the complete path
                const fullPath = `pmt2_ledger/${product.companyName}/${sanitizedAsin}/${sanitizedDocId}`;
                const pathValidation = validateFirestorePath(fullPath);
                
                if (!pathValidation.valid) {
                  console.warn(`[PPCBasicsDashboard] Path validation failed for review count: ${pathValidation.message}`);
                }
                
                const reviewDocRef = doc(db, 'pmt2_ledger', product.companyName, sanitizedAsin, sanitizedDocId);
                const docSnap = await getDoc(reviewDocRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    return Array.isArray(data?.entries) ? data.entries.length : 0;
                }
                return 0; // Return 0 if doc doesn't exist
            })(),
            // Fetch Bidding Group URL
            (async () => {
                const groupValue = product['Group'];
                if (groupValue) {
                    const docId = `16_ Bid Checker_Updater - Group ${groupValue}`;
                    const urlDocRef = doc(db, 'pmt2_clienturldatabase', product.companyName, 'items', docId);
                    const urlDocSnap = await getDoc(urlDocRef);
                    return urlDocSnap.exists() ? urlDocSnap.data().value ?? null : null;
                }
                return null;
            })(),
            // Fetch Old Values Map
            fetchLatestOldValuesMap(product.companyName, product.id)
        ]);

        // Process results
        if (results[0].status === 'fulfilled') {
            reviewCount = results[0].value ?? 0;
        } else {
            console.error('[PPCBasics] Error fetching review count:', results[0].reason);
        }

        if (results[1].status === 'fulfilled') {
            bidUrl = results[1].value;
        } else {
            console.error('[PPCBasics] Error fetching bidding group URL:', results[1].reason);
        }

        if (results[2].status === 'fulfilled') {
            fetchedOldValues = results[2].value;
        } else {
            console.error('[PPCBasics] Error fetching old values map:', results[2].reason);
        }

      } catch (error) {
        console.error('[PPCBasics] Error in fetchExtraData Promise.allSettled:', error);
      } finally {
        setTotalASINReviews(reviewCount);
        setBiddingGroupURL(bidUrl);
        setOldValuesMap(fetchedOldValues);
        setLoadingExtra(false);
      }
    };

    fetchExtraData();
  }, [product]);


  // Configuration for dashboard items in the desired order
  const dashboardItemsConfig = [
    { label: 'Active', field: 'Active', default: 'No', oldValueKey: 'Active' },
    { label: 'PPC Status', field: 'PPC Status', oldValueKey: 'PPC Status' },
    { label: 'JLS Active Status', field: 'JLS Active Status', default: '-', oldValueKey: 'JLS Active Status' },
    { label: 'Start Date', field: 'Start Date', format: 'excelDate', oldValueKey: 'Start Date' },
    { label: 'Time Active', field: 'Start Date', format: 'timeActive' }, // Calculated - No old value shown
    { label: 'Time Farming, if Actively Farming', field: 'Time Farming, if Actively Farming' }, // Assuming no direct old value
    { label: 'ASIN Manager', field: 'ASIN Manager', oldValueKey: 'ASIN Manager' },
    { label: 'Days Since Last Review', field: 'Days Since Last Review', format: 'round', oldValueKey: 'Days Since Last Review' }, // Use label as key
    { label: 'ASIN Review Cadence', field: 'ASIN Review Cadence', format: 'round', oldValueKey: 'ASIN Review Cadence' },
    { label: 'Total ASIN Reviews', field: null, source: totalASINReviews, oldValueKey: 'Total ASIN Reviews' }, // Use label as key
    { label: 'Product Family Name', field: 'Product Family Name', oldValueKey: 'Product Family Name' },
    { label: 'Group', field: 'Group', oldValueKey: 'Group' },
    { label: 'Bidding Group URL', field: null, source: biddingGroupURL }, // No old value shown
    { label: 'Allow Automatic Budget Updates', field: 'Allow Automatic Budget Updates', default: 'No', oldValueKey: 'Allow Automatic Budget Updates' },
    { label: 'Enable Dayparting', field: 'Enable Dayparting', default: 'No', oldValueKey: 'Enable Dayparting' },
    { label: 'Automatically Update Campaign Titles', field: 'Automatically Update Campaign Titles', default: 'No' }, // Assuming no direct old value
    { label: 'Campaign Title Style', field: 'Campaign Title Style' }, // Assuming no direct old value
    { label: 'Prevent Automatic Target Archiving', field: 'Prevent Automatic Target Archiving', format: 'excelDate', oldValueKey: 'Prevent Automatic Target Archiving' },
  ];

  // Split items for two columns
  const midPoint = Math.ceil(dashboardItemsConfig.length / 2);
  const column1ItemsConfig = dashboardItemsConfig.slice(0, midPoint);
  const column2ItemsConfig = dashboardItemsConfig.slice(midPoint);

  // Helper to get raw value, considering defaults and state sources
  const getRawValue = (item: any, productData: DocumentData | undefined | null) => {
      // Use item.source if it's defined (for state-managed values like totalASINReviews)
      if (item.source !== undefined) return item.source;
      // Otherwise, get value from product data
      if (!productData) return item.default ?? 'N/A';
      const value = productData[item.field];
      return (value === null || value === undefined || value === '') ? (item.default ?? 'N/A') : value;
  };


   // Helper to determine styling based on item label and raw value
  const getItemStyling = (label: string, rawValue: any, productData: DocumentData | undefined | null): { colorClass: string, icon: React.ReactNode } => {
      let colorClass = '';
      let icon: React.ReactNode = null;
      const yesNoCheck = (val: any) => String(val).toLowerCase() === 'yes' ? { c: 'text-green-700', i: <CheckCircle size={16}/> } : { c: 'text-yellow-500', i: null };

      switch (label) {
          case 'Active':
          case 'Allow Automatic Budget Updates':
          case 'Enable Dayparting':
          case 'Automatically Update Campaign Titles':
              const ynStyle = yesNoCheck(rawValue);
              colorClass = ynStyle.c;
              icon = ynStyle.i;
              break;
          case 'PPC Status':
              if (rawValue === 'Enabled' || rawValue === 'Farming') { colorClass = 'text-green-700'; icon = <CheckCircle size={16}/>; }
              else if (rawValue !== 'N/A' && rawValue !== '-') { colorClass = 'text-yellow-500'; }
              break;
          case 'Days Since Last Review':
              const days = parseSafeFloat(rawValue);
              const cadence = parseSafeFloat(productData?.['ASIN Review Cadence']);
              if (days !== null && cadence !== null) {
                  if (days <= cadence) { colorClass = 'text-green-700'; icon = <CheckCircle size={16}/>; }
                  else { colorClass = 'text-red-600'; icon = <AlertCircle size={16}/>; }
              }
              break;
          case 'Group':
              const groupValStr = String(rawValue).toLowerCase();
              const isNum = !isNaN(parseFloat(groupValStr)) && isFinite(Number(groupValStr));
              if (groupValStr === 'yes' || isNum) { colorClass = 'text-green-700'; icon = <CheckCircle size={16}/>; }
              else if (groupValStr === 'no') { colorClass = 'text-yellow-500'; }
              else if (rawValue !== 'N/A' && rawValue !== '-') { colorClass = 'text-red-600'; icon = <AlertCircle size={16}/>; }
              break;
          default:
              break;
      }
      return { colorClass, icon };
  };

  // Helper to format the final display value
  const formatDisplayValue = (item: any, rawValue: any): React.ReactNode => {
      if (rawValue === 'N/A' || rawValue === null || rawValue === undefined) return '-';

      if (item.format === 'excelDate') {
          const dateNum = parseSafeFloat(rawValue);
          return formatDateMMDDYYYY(excelSerialDateToJSDate(dateNum));
      }
      if (item.format === 'round') {
          const num = parseSafeFloat(rawValue);
          return num !== null ? Math.round(num).toString() : '-';
      }
       if (item.label === 'Time Active') {
           const excelDateNum = parseSafeFloat(rawValue); // rawValue comes from item.field ('Start Date')
           const jsDate = excelSerialDateToJSDate(excelDateNum);
           if (!jsDate) return '-';
           const currentDate = new Date();
           if (jsDate.getTime() > currentDate.getTime()) return '0 Mos';
           const monthsDiff = (currentDate.getFullYear() - jsDate.getFullYear()) * 12 + (currentDate.getMonth() - jsDate.getMonth());
           const nonNegativeMonthsDiff = Math.max(0, monthsDiff);
           const years = Math.floor(nonNegativeMonthsDiff / 12);
           const months = nonNegativeMonthsDiff % 12;
           if (years > 0) return `${years} Yr${years > 1 ? 's' : ''} ${months > 0 ? `${months} Mo${months > 1 ? 's' : ''}` : ''}`.trim();
           return `${months} Mo${months !== 1 ? 's' : ''}`;
       }
       if (item.label === 'Bidding Group URL' && typeof rawValue === 'string' && rawValue.startsWith('http')) {
           return <a href={rawValue} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Link</a>;
       }

      return formatDefault(rawValue);
  };

  // Use the local loading state for this component's specific fetches
  const combinedLoading = loadingExtra;

  if (!product) {
       return (
            <Card>
                <CardHeader><CardTitle className="text-2xl font-bold">PPC Basics</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-500">Select a product to view PPC Basics.</p></CardContent>
            </Card>
        );
  }

  return (
    <Card>
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">PPC Basics</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {/* Column 1 */}
          <Table>
             <TableHeader>
                <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Old Value</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
              {column1ItemsConfig.map((item) => {
                const rawValue = getRawValue(item, product);
                const { colorClass, icon } = getItemStyling(item.label, rawValue, product);
                const displayValue = formatDisplayValue(item, rawValue);
                const oldValueKey = item.oldValueKey ?? item.label;
                const oldValue = oldValuesMap?.[oldValueKey];
                return (
                  <ItemDisplay
                    key={item.label}
                    label={item.label}
                    value={displayValue}
                    currentValue={rawValue}
                    oldValue={oldValue}
                    isLoading={combinedLoading}
                    colorClass={colorClass}
                    icon={icon}
                    labelKey={oldValueKey}
                  />
                );
              })}
            </TableBody>
          </Table>
          {/* Column 2 */}
          <Table>
             <TableHeader>
                <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Old Value</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
              {column2ItemsConfig.map((item) => {
                 const rawValue = getRawValue(item, product);
                 const { colorClass, icon } = getItemStyling(item.label, rawValue, product);
                 const displayValue = formatDisplayValue(item, rawValue);
                 const oldValueKey = item.oldValueKey ?? item.label;
                 const oldValue = oldValuesMap?.[oldValueKey];
                 return (
                   <ItemDisplay
                     key={item.label}
                     label={item.label}
                     value={displayValue}
                     currentValue={rawValue}
                     oldValue={oldValue}
                     isLoading={combinedLoading}
                     colorClass={colorClass}
                     icon={icon}
                     labelKey={oldValueKey}
                   />
                 );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PPCBasicsDashboard;